// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Browser } from "@capacitor/browser";
import { useGenerateDeepLink } from "@src/appV2/DeepLinks";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useCallback } from "react";

import { useGenerateStripeExpressSignInLink } from "../api/useGenerateStripeExpressSignInLink";

export function useGoToStripeExpressSignIn(): {
  goToStripeExpressSignIn: () => void;
  isGoToStripeExpressSignInLoading: boolean;
} {
  const { showErrorToast } = useToast();
  const stripeReturnLinkForceRedirect = useCbhFlag(
    CbhFeatureFlag.STRIPE_RETURN_LINK_FORCE_REDIRECT,
    {
      defaultValue: false,
    }
  );

  const {
    mutateAsync: generateStripeExpressSignInLink,
    isLoading: isGenerateStripeExpressSignInLinkLoading,
  } = useGenerateStripeExpressSignInLink();

  const { mutateAsync: generateDeepLink, isLoading: isGenerateDeepLinkLoading } =
    useGenerateDeepLink();

  const isGoToStripeExpressSignInLoading =
    isGenerateDeepLinkLoading || isGenerateStripeExpressSignInLinkLoading;

  const goToStripeExpressSignIn = useCallback(async () => {
    try {
      const returnUrl = await generateDeepLink({
        link: `${environmentConfig.REACT_APP_WEB_APP_URL}${location.pathname}`,
        skipPreview: stripeReturnLinkForceRedirect,
      });
      const { data: link } = await generateStripeExpressSignInLink({ returnUrl });

      await Browser.open({ url: link });
    } catch (error) {
      showErrorToast("Failed to open Stripe, please try again!");
      logEvent(APP_V2_APP_EVENTS.GENERATE_STRIPE_EXPRESS_SIGN_IN_LINK_FAILURE, { error });
    }
  }, [
    generateDeepLink,
    generateStripeExpressSignInLink,
    showErrorToast,
    stripeReturnLinkForceRedirect,
  ]);

  return { goToStripeExpressSignIn, isGoToStripeExpressSignInLoading };
}
